import {useCallback, useEffect, useState} from 'react';
import cx from 'classnames';

import {useActionPlanPageQuery} from '#apollo/schema';
import {AnimatedText, Icon, Link, Text} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {ActionPlanFilterModal} from '#component/modal';
import {ActionPlanFilters} from '#component/modal/ActionPlanFilterModal';
import {getEmptyFilters} from '#component/modal/ActionPlanFilterModal/ActionPlanFilterModal.utils';
import {FullscreenLoading, Hero, InternalNavBar} from '#component/partial';
import {EventType, PageViewEvent, useAnalytics} from '#component/provider/AnalyticsProvider';
import {Transition} from '#component/util';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {useViewportSize} from '#hook';
import {pageSlideInKeyframe} from '#style/keyframes.css';
import {sprinkles} from '#style/sprinkles';

import {ActionPlanPage} from '../ActionPlan/ActionPlan.types';
import {RecommendationTable} from './component';
import {Buttons} from './component/Buttons/Buttons';
import {isFiltered} from './EditActionPlan.utils';

import {RecommendationInput} from './EditActionPlan.types';

export const EditActionPlanScreen = () => {
  const {track} = useAnalytics();
  const {isDesktop} = useViewportSize();
  const {data, loading} = useActionPlanPageQuery();
  const [recommendationInput, setRecommendationInput] = useState<RecommendationInput[]>([]);
  const [touched, setTouched] = useState(false);
  const [filters, setFilters] = useState<ActionPlanFilters>(getEmptyFilters());
  const {actionPlans, submissionId} =
    data?.me?.mostRecentLabSubmissionWithResults?.userSurveySubmission?.surveyAssessment ?? {};
  const actionPlan = actionPlans?.[0];

  useEffect(() => {
    if (!actionPlan) {
      setRecommendationInput([]);
    } else {
      setRecommendationInput(
        actionPlan.adjustments.map(
          (recommendation): RecommendationInput => ({
            id: recommendation.id,
            healthFactorId: recommendation.healthFactorId || '',
            selected: actionPlan.active ? recommendation.selected : recommendation.recommended,
          }),
        ),
      );
    }
  }, [actionPlan]);

  const handleReset = useCallback(() => {
    setRecommendationInput(
      (actionPlan?.adjustments || []).map(
        (recommendation: ActionPlanPage.ActionPlanAdjustment): RecommendationInput => ({
          id: recommendation.id,
          healthFactorId: recommendation.healthFactorId || '',
          selected: recommendation.recommended,
        }),
      ),
    );
  }, [actionPlan, setRecommendationInput]);

  useEffect(() => {
    if (!loading) {
      track<PageViewEvent>({
        evtName: EventType.PageView,
        pageName: 'Edit Action Plan',
      });
    }
  }, [loading, track]);

  if (loading) {
    return <FullscreenLoading text="Loading your current action plan..." />;
  }

  return (
    <div className={sprinkles({bg: 'cotton'})}>
      <InternalNavBar showLogo />

      <Hero showOverlap={false} variant="sunrise">
        <Transition
          appear
          className={cx(
            sprinkles({layout: 'grid', items: 'center', pY: [80, 128, 192]}),
            pageSlideInKeyframe,
          )}
          show>
          <AnimatedText
            className={sprinkles({colSpan: [4, 6, 4], colStart: [1, 2], w: 'full'})}
            color="deepBurgundy"
            variant="heading1">
            Customize my Action Plan
          </AnimatedText>

          <div
            className={sprinkles({
              colSpan: [4, 6, 5],
              colStart: [1, 2, 7],
              content: 'start',
              dir: 'col',
              flex: 'flex',
              gap: 32,
            })}>
            <p>
              <AnimatedText as="span" color="inherit" variant="bodyLarge">
                Want more control over how you age?
              </AnimatedText>
              <br />
              <AnimatedText as="span" color="inherit" variant="bodyLarge">
                {"Let's start with your Action Plan."}
              </AnimatedText>
            </p>
          </div>
        </Transition>
      </Hero>

      <div className={sprinkles({layout: 'grid', pY: 80})}>
        <div
          className={sprinkles({
            colSpan: [4, 6, 10],
            colStart: 1,
            dir: 'col',
            flex: 'flex',
            gap: 8,
          })}>
          <Text as="h2" color="deepBurgundy" variant="heading3">
            <Icon
              color="deepBurgundy"
              name="MagicWand"
              weight="regular"
              size={'large'}
              className={sprinkles({
                mT: 4,
                mR: 8,
              })}
            />
            Suggested goals for you:
          </Text>

          <Text as="p" color="doveGray">
            {
              "Using your unique DNA methylation profile, we've suggested lifestyle goals that may have the largest impact on your TallyAge. You can customize your goals based on preference."
            }
          </Text>
        </div>

        <div
          className={sprinkles({
            colSpan: [4, 8, 12],
            colStart: 1,
            content: 'between',
            dir: ['col', 'row'],
            flex: 'flex',
            gap: 32,
            items: 'center',
          })}>
          {actionPlan && (
            <Buttons
              actionPlan={actionPlan}
              recommendationInput={recommendationInput}
              touched={touched}
              hidden={!isDesktop}
            />
          )}
          <div className={sprinkles({flex: 'flex', gap: 32, w: ['full', 'maxContent']})}>
            <ModalRoot>
              <Text
                as={ModalTrigger}
                className={sprinkles({
                  flex: 'flex',
                  gap: 8,
                  items: 'center',
                  shrink: 0,
                  w: 'maxContent',
                })}>
                <Link as="span">Filter</Link>
                <Icon
                  color={isFiltered(filters) ? 'eggplant' : 'doveGray'}
                  name="Funnel"
                  size={24}
                />
              </Text>
              <ActionPlanFilterModal
                adjustments={actionPlan?.adjustments || []}
                filters={filters}
                setFilters={setFilters}
              />
            </ModalRoot>

            <Text
              className={sprinkles({
                flex: 'flex',
                gap: 8,
                items: 'center',
                shrink: 0,
                w: 'maxContent',
              })}
              color="doveGray">
              <Link as="span" onClick={handleReset}>
                {'Tally Recommends'}
              </Link>
              <Icon color="doveGray" name="Star" size={24} />
            </Text>
          </div>
        </div>
        {actionPlan && submissionId && (
          <div
            className={sprinkles({
              colStart: 1,
              colSpan: [4, 8, 12],
              overflow: 'auto',
              w: 'full',
            })}>
            <RecommendationTable
              recommendations={actionPlan.adjustments}
              surveySubmissionId={submissionId}
              filters={filters}
              recommendationInput={recommendationInput}
              onChangeSelection={selection => {
                if (!touched) {
                  setTouched(true);
                }
                setRecommendationInput(selection);
              }}
            />
            <Buttons
              actionPlan={actionPlan}
              recommendationInput={recommendationInput}
              touched={touched}
              hidden={isDesktop}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const EditActionPlan = withRequireUserState(EditActionPlanScreen, {userState: 'verified'});
